import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import './ui.css';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'lg';
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
};

export function Button({ variant = 'primary', size = 'md', loading, block, icon, children, disabled, className, ...rest }: ButtonProps) {
  return (
    <button
      className={['btn', `btn-${variant}`, `btn-${size}`, block && 'btn-block', className].filter(Boolean).join(' ')}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner small /> : icon}
      <span>{children}</span>
    </button>
  );
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string | null;
  trailing?: ReactNode;
};

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field({ label, hint, error, trailing, id, ...rest }, ref) {
  const inputId = id ?? `f-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  return (
    <div className={`field${error ? ' field-error' : ''}`}>
      <label htmlFor={inputId}>{label}</label>
      <div className="field-control">
        <input ref={ref} id={inputId} aria-invalid={!!error || undefined} aria-describedby={describedBy} {...rest} />
        {trailing ? <div className="field-trailing">{trailing}</div> : null}
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="field-message" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="field-hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

export function Spinner({ small }: { small?: boolean }) {
  return <span className={`spinner${small ? ' spinner-small' : ''}`} aria-hidden="true" />;
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'error' | 'success'; children: ReactNode }) {
  return (
    <div className={`notice notice-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Logo({ height = 28 }: { height?: number }) {
  return <img src="/logo.png" alt="EducAitor" height={height} style={{ height, width: 'auto', display: 'block' }} />;
}

/** The EducAitor mark as an avatar: EducAitor admins and the EducAitor channel. */
export function TeamAvatar({ size = 36 }: { size?: number }) {
  return (
    <span className="team-avatar" style={{ width: size, height: size }} aria-hidden="true">
      <img src="/favicon.svg" alt="" style={{ width: size * 0.62, height: size * 0.62 }} />
    </span>
  );
}

export function Initials({ name, size = 36 }: { name: string; size?: number }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  return (
    <span className="initials" style={{ width: size, height: size, fontSize: size * 0.36 }} aria-hidden="true">
      {initials || '?'}
    </span>
  );
}

export function FullPageSpinner() {
  return (
    <div className="full-page-center">
      <Spinner />
    </div>
  );
}
